import {keyBy} from 'lodash-es';
import {spotheroGet} from 'api';
import {sendSentryException} from 'utils/error-handling';
import type {
    RawRatingRuleGroupResponse,
    RawEnabledRatingRuleGroupsResponse,
    RawTransientPricingAlgorithmRawResponse,
} from 'api/rates/rates.types';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

/**
 * Formats the response from the get rating rule groups endpoint
 *
 * @memberof module:api/rates/dto
 */
const formatGetRatingRuleGroupsResponse = (
    response: RawRatingRuleGroupResponse[]
) => {
    const formattedResponse = camelizeKeys(response);

    return formattedResponse;
};

/**
 * Gets rating rule groups for a facility
 *
 *
 * @see '/api/v1/facilities/{facilityId}/rating-rule-groups/'
 */
export const getRatingRuleGroups = async (facilityId: number) => {
    try {
        const response = await spotheroGet<RawRatingRuleGroupResponse[]>(
            `/facilities/${facilityId}/rating-rule-groups/`
        );

        const formattedResponse = formatGetRatingRuleGroupsResponse(
            response?.data?.data?.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Format the given response object to get the enabled rating rule groups.
 *
 * @memberof module:api/rates/dto
 */
const formatGetEnabledRatingRuleGroupsResponse = (
    response: RawEnabledRatingRuleGroupsResponse,
    isAirportPage: boolean
) => {
    const formattedResponse = camelizeKeys(response);
    const ruleGroups = Object.values(formattedResponse?.ruleGroups || {});

    const filteredRuleGroups = ruleGroups.filter((ruleGroup) => {
        if (isAirportPage) {
            return ruleGroup.showOnAirport === true;
        } else {
            return (
                ruleGroup.showOnAirport === false &&
                ruleGroup.showOnTransient === true
            );
        }
    });

    return filteredRuleGroups;
};

/**
 * Retrieves the enabled rating rule groups for a specific facility.
 *
 * @see '/api/v1/ratings/facilities/{facilityId}/rates/'
 */
export const getEnabledRatingRuleGroups = async (
    facilityId: number,
    isAirportPage: boolean
) => {
    try {
        const response = await spotheroGet<RawEnabledRatingRuleGroupsResponse>(
            `/ratings/facilities/${facilityId}/rates/`
        );

        const formattedResponse = formatGetEnabledRatingRuleGroupsResponse(
            response?.data?.data,
            isAirportPage
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the request for transient pricing algorithms.
 */
const formatGettransientPricingAlgorithmsRequest = (request) => {
    const updatedRequest = {
        ...request,
        facilityIds: request.facilityIds.toString(),
    };

    const formattedRequest = convertKeysToSnakeCase(updatedRequest);

    return formattedRequest;
};

/**
 * Formats the response of transient pricing algorithms.
 *
 * @memberof module:api/rates/dto
 */
const formatGetTransientPricingAlgorithmsResponse = (
    response: RawTransientPricingAlgorithmRawResponse[]
) => {
    const formattedResponse = camelizeKeys(response);
    const pricingAlgorithms = keyBy(formattedResponse, 'facilityId');

    return pricingAlgorithms;
};

/**
 * Retrieves the transient pricing algorithms for the given facility IDs.
 *
 * @see '/api/v1/ratings/facilities/rates/'
 */
export const getTransientPricingAlgorithms = async (facilityIds: number[]) => {
    try {
        const formattedRequest = formatGettransientPricingAlgorithmsRequest({
            facilityIds,
        });

        const response = await spotheroGet<
            RawTransientPricingAlgorithmRawResponse[]
        >(`/ratings/facilities/rates/`, formattedRequest);

        const formattedResponse = formatGetTransientPricingAlgorithmsResponse(
            response?.data?.data?.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
